import { type Canvas, Line } from "fabric";
import { LINE_LAYER_TYPE } from "../constants.ts";
import type {
  CanvasSize,
  LineLayer,
  LineLayerProperties,
  LineLayerSettings,
} from "../types.ts";
import { createLayer, getLayers } from "./layer.ts";
import { updateLayerStrokeStyle } from "./stroke.ts";

type NormalizableProperties = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  left: number;
  top: number;
};

export const createLineLayer = ({
  fabricCanvas,
  canvasSize,
  name,
  positionIndex,
  options,
}: {
  fabricCanvas: Canvas;
  canvasSize: CanvasSize;
  name?: string;
  positionIndex?: number;
  options: LineLayerProperties;
}) => {
  const { x1, x2, y1, y2, left, top } = fitLineLayerPropertiesToCanvas({
    x1: options.x1 ?? (canvasSize.width * 1) / 4,
    y1: options.y1 ?? canvasSize.height / 2,
    x2: options.x2 ?? (canvasSize.width * 3) / 4,
    y2: options.y2 ?? canvasSize.height / 2,
    left: 0,
    top: 0,
  });

  const line = new Line([x1, y1, x2, y2], {
    stroke: options.stroke ?? "rgba(0, 0, 0, 1)",
    strokeWidth: options.strokeWidth ?? 10,
    left,
    top,
    originX: "center",
    originY: "center",
    angle: options.rotation ?? 0,
  }) as LineLayer;
  updateLayerStrokeStyle(line, options.strokeStyle ?? "solid");

  addLineLayerEventListeners(line, fabricCanvas, canvasSize);

  const currentLayerCount = getLayers(fabricCanvas).length;

  const lineLayer = createLayer({
    fabricCanvas,
    layer: line,
    name: name ?? `Line ${currentLayerCount + 1}`,
    positionIndex: positionIndex ?? currentLayerCount,
    layerType: LINE_LAYER_TYPE,
  });

  return lineLayer;
};

// XXX: We add directly event listener to apply directly the scaling operations to the line layers, so the scaling property corresponds only to the display scale.
export const addLineLayerEventListeners = (
  layer: LineLayer,
  fabricCanvas: Canvas,
  canvasSize: CanvasSize,
) => {
  layer.on({
    modified(this: LineLayer) {
      const { width, scaleX } = this;
      const displayXScale = fabricCanvas.getWidth() / canvasSize.width;
      const displayYScale = fabricCanvas.getHeight() / canvasSize.height;
      this.set({
        width: (width * scaleX) / displayXScale,
        scaleX: displayXScale,
        scaleY: displayYScale,
      });
      this.setCoords();
    },
  });
};

export const duplicateLineLayer = (
  fabricCanvas: Canvas,
  canvasSize: CanvasSize,
  lineLayer: LineLayer,
) => {
  const { x1, y1, x2, y2 } = normalizeLineLayerProperties({
    x1: lineLayer.x1,
    y1: lineLayer.y1,
    x2: lineLayer.x2,
    y2: lineLayer.y2,
    left: lineLayer.left,
    top: lineLayer.top,
  });
  return createLineLayer({
    fabricCanvas,
    canvasSize,
    options: {
      x1,
      y1,
      x2,
      y2,
      stroke: lineLayer.stroke,
      strokeWidth: lineLayer.strokeWidth,
      strokeStyle: lineLayer.strokeStyle,
      rotation: lineLayer.angle,
    },
  });
};

export const updateLineLayer = (
  lineLayer: LineLayer,
  options: Partial<Required<LineLayerSettings>>,
) => {
  lineLayer.canvas?.fire("layer:line:changed", {
    layer: lineLayer,
    changes: options,
  });
  lineLayer.set({
    strokeWidth: options.strokeWidth ?? lineLayer.strokeWidth,
    angle: options.rotation ?? lineLayer.angle,
    stroke: options.stroke ?? lineLayer.stroke,
  });
  if (options.strokeStyle !== undefined) {
    updateLayerStrokeStyle(lineLayer, options.strokeStyle);
  }
  lineLayer.setCoords();
};

export const normalizeLineLayerProperties = (
  normalizableProperties: NormalizableProperties,
): NormalizableProperties => ({
  x1: normalizableProperties.x1 + normalizableProperties.left,
  y1: normalizableProperties.y1 + normalizableProperties.top,
  x2: normalizableProperties.x2 + normalizableProperties.left,
  y2: normalizableProperties.y2 + normalizableProperties.top,
  left: normalizableProperties.left,
  top: normalizableProperties.top,
});

export const fitLineLayerPropertiesToCanvas = (
  normalizableProperties: NormalizableProperties,
): NormalizableProperties => ({
  x1: 0,
  y1: 0,
  x2: normalizableProperties.x2 - normalizableProperties.x1,
  y2: normalizableProperties.y2 - normalizableProperties.y1,
  left: (normalizableProperties.x1 + normalizableProperties.x2) / 2,
  top: (normalizableProperties.y1 + normalizableProperties.y2) / 2,
});
